import { useState } from "react";
import { Link } from "react-router-dom";
import LayoutWide from "../layouts/LayoutWide.jsx";
import Spinner from "../components/Spinner.jsx";
import { useRecipesQuery, useDeleteRecipe } from "../hooks/useRecipesQuery.js";
import Card from "../components/ui/Card.jsx";
import Badge from "../components/ui/Badge.jsx";
import EmptyState from "../components/ui/EmptyState.jsx";
import ErrorState from "../components/ui/ErrorState.jsx";
import PageHeader from "../components/ui/PageHeader.jsx";

function ingredientSummary(raw) {
  if (!raw) return null;
  try {
    const rows = JSON.parse(raw);
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const names = rows.filter(r => r.name?.trim()).map(r => r.name.trim());
    if (names.length === 0) return null;
    const preview = names.slice(0, 3).join(", ");
    return names.length > 3 ? `${preview} +${names.length - 3} more` : preview;
  } catch {
    return null;
  }
}

const TYPE_LABELS = {
  liquid_culture: "Liquid Culture",
  bulk_spawn:     "Bulk Spawn",
  substrate:      "Substrate",
  other:          "Other",
};

const TYPE_BADGE_COLOR = {
  liquid_culture: "info",
  bulk_spawn:     "warning",
  substrate:      "emerald",
  other:          "gray",
};

export default function Recipes() {
  const { data: recipes, isLoading, isError } = useRecipesQuery();
  const deleteRecipe = useDeleteRecipe();
  const [confirmId, setConfirmId] = useState(null);

  if (isLoading) return <LayoutWide title="Recipes"><Spinner /></LayoutWide>;
  if (isError) return <LayoutWide title="Recipes"><ErrorState>Failed to load recipes.</ErrorState></LayoutWide>;

  const grouped = {};
  for (const r of (recipes ?? [])) {
    const t = r.recipe_type;
    if (!grouped[t]) grouped[t] = [];
    grouped[t].push(r);
  }
  const typeOrder = ["liquid_culture", "bulk_spawn", "substrate", "other"];

  async function handleDelete(id) {
    await deleteRecipe.mutateAsync(id);
    setConfirmId(null);
  }

  return (
    <LayoutWide title="Recipes">
      <PageHeader title="Recipes" actionLabel="+ New Recipe" actionTo="/recipes/new" />

      {(recipes ?? []).length === 0 && (
        <div className="text-center py-20">
          <EmptyState
            icon="🧪"
            title="No recipes yet"
            description="Build up your recipe library — liquid cultures, substrates, spawn formulas."
            action={
              <Link
                to="/recipes/new"
                className="text-sm bg-brand text-white rounded-control px-4 py-2 hover:bg-brand-hover transition-colors font-medium"
              >
                Add your first recipe
              </Link>
            }
          />
        </div>
      )}

      {typeOrder.filter(t => grouped[t]?.length).map(type => (
        <div key={type} className="mb-8">
          <h2 className="text-sm font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
            {TYPE_LABELS[type]}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {grouped[type].map(recipe => (
              <Card key={recipe.id} variant="outline" padding="none" className="p-4 flex flex-col gap-2 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/recipes/${recipe.id}`} className="font-medium text-gray-800 dark:text-gray-100 hover:text-sky-700 dark:hover:text-sky-400 leading-tight">
                    {recipe.name}
                  </Link>
                  <Badge color={TYPE_BADGE_COLOR[recipe.recipe_type]} className="shrink-0">
                    {TYPE_LABELS[recipe.recipe_type]}
                  </Badge>
                </div>

                {ingredientSummary(recipe.ingredients) && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{ingredientSummary(recipe.ingredients)}</p>
                )}

                <div className="flex items-center gap-3 mt-auto pt-1">
                  <Link to={`/recipes/${recipe.id}`} className="text-xs text-sky-700 dark:text-sky-400 hover:underline">View</Link>
                  <Link to={`/recipes/${recipe.id}/edit`} className="text-xs text-gray-500 dark:text-gray-400 hover:underline">Edit</Link>
                  {confirmId === recipe.id ? (
                    <span className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                      Delete?
                      <button onClick={() => handleDelete(recipe.id)} className="font-semibold hover:underline">Yes</button>
                      <span>/</span>
                      <button onClick={() => setConfirmId(null)} className="hover:underline">No</button>
                    </span>
                  ) : (
                    <button onClick={() => setConfirmId(recipe.id)} className="text-xs text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 ml-auto">Delete</button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </LayoutWide>
  );
}
